import * as React from 'react';
import { cn } from 'lib/utils';
import { useCallback, useEffect, useState } from 'react';
import {
    AudioInputPopover,
    InsertMode,
} from 'components/ui/audio-input-popover';
import {
    AudioInputDialog,
    InsertAction,
} from 'components/ui/audio-input-dialog';

export interface TextareaProps
    extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
    enableAudio?: boolean;
    audioMode?: 'popover' | 'dialog';
    transcriptionPostProcessing?: string | boolean;
    allowCustomPostProcessing?: boolean;
    onTranscriptionComplete?: (text: string) => void;
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
    (
        {
            className,
            enableAudio = false,
            audioMode = 'dialog',
            transcriptionPostProcessing = false,
            allowCustomPostProcessing = false,
            onTranscriptionComplete,
            ...props
        },
        ref,
    ) => {
        const [textareaElement, setTextareaElement] =
            useState<HTMLTextAreaElement | null>(null);
        const [value, setValue] = useState(props.value || '');

        useEffect(() => {
            if (props.value !== undefined) {
                setValue(props.value);
            }
        }, [props.value]);

        const measuredRef = useCallback(
            (node: HTMLTextAreaElement | null) => {
                setTextareaElement(node);
                if (typeof ref === 'function') {
                    ref(node);
                } else if (ref) {
                    ref.current = node;
                }
            },
            [ref],
        );

        const triggerResize = useCallback(
            (element: HTMLTextAreaElement | null) => {
                if (!element) return;

                // Store current scroll position
                const originalScrollY = window.scrollY;

                element.style.height = 'auto';
                const scrollHeight = element.scrollHeight;
                // Plus 2px buffer to prevent scrollbar from appearing
                element.style.height = `${scrollHeight + 2}px`;

                // Restore scroll position immediately after height change
                window.scrollTo(0, originalScrollY);
            },
            [],
        );

        useEffect(() => {
            triggerResize(textareaElement);
        }, [textareaElement, value, props.defaultValue, triggerResize]);

        const handleTranscription = (
            text: string,
            mode: InsertMode | InsertAction,
        ) => {
            const newValue =
                mode === 'replace'
                    ? text
                    : mode === 'append' || mode === 'insert'
                      ? `${value} ${text}`.trim()
                      : text;

            setValue(newValue);

            if (props.onChange) {
                const event = {
                    target: { value: newValue },
                } as React.ChangeEvent<HTMLTextAreaElement>;
                props.onChange(event);
            }

            onTranscriptionComplete?.(text);

            // Trigger resize after value change
            setTimeout(() => triggerResize(textareaElement), 0);
        };

        const postProcessing =
            typeof transcriptionPostProcessing === 'string'
                ? transcriptionPostProcessing
                : transcriptionPostProcessing === true
                  ? 'Clean up filler words and fix punctuation'
                  : null;

        const textarea = (
            <textarea
                className={cn(
                    'flex max-h-[800px] min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
                    enableAudio && 'pr-10',
                    className,
                )}
                ref={measuredRef}
                onInput={(e) => {
                    props.onInput?.(e);
                    triggerResize(e.currentTarget);
                }}
                onWheel={(e) => {
                    props.onWheel?.(e);
                    const target = e.target as HTMLTextAreaElement;
                    const isScrollable =
                        target.scrollHeight > target.clientHeight;

                    if (!isScrollable) {
                        return;
                    }

                    const isScrollingUp = e.deltaY < 0;
                    const isScrollingDown = e.deltaY > 0;
                    const isAtTop = target.scrollTop === 0;
                    const isAtBottom =
                        target.scrollTop + target.clientHeight >=
                        target.scrollHeight;

                    if (
                        (isScrollingUp && isAtTop) ||
                        (isScrollingDown && isAtBottom)
                    ) {
                        return;
                    }

                    e.stopPropagation();
                }}
                {...props}
                value={value}
                onChange={(e) => {
                    setValue(e.target.value);
                    props.onChange?.(e);
                }}
                data-1p-ignore
            />
        );

        if (!enableAudio) {
            return textarea;
        }

        return (
            <div className="relative w-full">
                {textarea}
                <div className="absolute right-1 top-2">
                    {audioMode === 'popover' ? (
                        <AudioInputPopover
                            onTranscriptionComplete={handleTranscription}
                            postProcessing={postProcessing}
                            hasExistingText={!!value}
                            disabled={props.disabled}
                        />
                    ) : (
                        <AudioInputDialog
                            onTranscriptionComplete={handleTranscription}
                            postProcessing={postProcessing}
                            allowCustomPostProcessing={
                                allowCustomPostProcessing
                            }
                            disabled={props.disabled}
                        />
                    )}
                </div>
            </div>
        );
    },
);
Textarea.displayName = 'Textarea';

export { Textarea };
